import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, ChevronDown, Clock, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { fmtNum, fmtPrice, formatApiError, raceTime, RECENT } from "@/lib/api";
import { RaceTypeBadge } from "@/components/race/RaceTypeBadge";
import { RaceFieldHeading } from "@/components/race/RaceField";
import { FinalSheet, FirstStarters } from "@/components/race/FinalSheet";
import { useAuth } from "@/context/AuthContext";
import { auDate, byMeeting, Chip, dayLabel, FeatureButton, MeetingCards, PREF_BADGE } from "@/pages/Program";

const RaceSheetBody = ({ race, i, onUpdated }) => {
  const isAdmin = useAuth().user?.role === "admin";
  const adjust = isAdmin && onUpdated ? async (number, points) => {
    try {
      const { data } = await api.put(`/races/${race.id}/adjust`, { number, points });
      onUpdated(data);
      toast.success(points ? `#${number} adjusted ${points > 0 ? "+" : ""}${points}: race re-rated` : `#${number} adjustment cleared`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  } : undefined;
  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {race.evaluation.preferred && <span className="print-preferred text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500 text-[#080C14] shadow-[0_0_12px_rgba(16,185,129,0.45)]" data-testid={`sheet-preferred-badge-${i}`}>Preferred Race</span>}
          <span className="eyebrow"><span className="text-amber-400">{raceTime(race)}</span> • {race.race_name}</span>
        </div>
        {isAdmin && <Link to={`/race/${race.id}`} className="no-print inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors shrink-0" data-testid={`sheet-race-detail-link-${i}`}>
          Full detail <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>}
      </div>
      <RaceFieldHeading race={race} prefix={`sheet-heading-${i}`} />
      <FinalSheet race={race} ratings={race.evaluation.ratings || []} testId={`sheet-final-${i}`} breakdown={isAdmin} onAdjust={adjust} />
      <FirstStarters list={race.evaluation.first_starters} testId={`sheet-first-starters-${i}`} />
    </>
  );
};

export const RaceSheet = ({ race, i }) => (
  <section className={`panel p-5 space-y-4 fade-up print-race ${race.evaluation.preferred ? "border-emerald-500/60 shadow-[inset_4px_0_0_#10B981]" : ""}`} style={{ animationDelay: `${i * 60}ms` }} data-testid={`sheet-race-${i}`}>
    <RaceSheetBody race={race} i={i} />
  </section>
);

const SheetRow = ({ race, i, open, onOpen, onUpdated }) => {
  const top = race.evaluation.ratings?.[0];
  const pref = race.evaluation.preferred;
  return (
    <div className={`panel transition-colors ${pref ? "bg-emerald-500/[0.06] shadow-[inset_4px_0_0_#10B981] border-emerald-500/60" : ""}`} data-testid={`sheets-race-${i}`}>
      <div className="flex items-center gap-3 sm:gap-4 p-4 cursor-pointer" onClick={onOpen} data-testid={`sheets-race-toggle-${i}`}>
        <span className="num text-amber-400 font-bold w-24 shrink-0 whitespace-nowrap">{raceTime(race)}</span>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold">{race.venue} R{race.race_number}
            {pref && <span className={PREF_BADGE} data-testid={`sheets-preferred-badge-${i}`}>Preferred Race</span>}
            <span className="ml-2"><RaceTypeBadge type={race.race_type} testId={`sheets-race-type-${i}`} /></span>
          </div>
          <div className="text-xs text-slate-400 truncate">{[race.race_class, race.distance && `${fmtNum(race.distance, 0)}m`, `${race.evaluation.field_size} runners`, race.track_condition].filter(Boolean).join(" • ")}</div>
        </div>
        {top && <div className="hidden sm:block text-right text-sm"><span className="text-slate-500 text-xs mr-2">Top</span><span className="text-white">{top.number} {top.name}</span> <span className="num text-amber-400 font-bold ml-1">{fmtPrice(top.price)}</span></div>}
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && <div className="px-1 sm:px-4 pb-5 space-y-4" data-testid={`sheets-race-detail-${i}`}><RaceSheetBody race={race} i={i} onUpdated={onUpdated} /></div>}
    </div>
  );
};

const MeetingSheets = ({ g, onBack, onUpdated }) => {
  const [opened, setOpened] = useState([]);
  return (
    <section className="space-y-3" data-testid="sheets-meeting">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 pb-3">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="sheets-back-button"><ArrowLeft className="h-4 w-4 mr-1.5" />Back to meetings</Button>
        <h2 className="font-display text-2xl font-bold text-white" data-testid="sheets-meeting-title">
          {[g.venue, g.state, g.track, `${g.races.length} races`].filter(Boolean).join(" • ")}
        </h2>
      </div>
      {g.races.map((r, j) => (
        <SheetRow key={r.id} race={r} i={j} open={opened.includes(r.id)} onUpdated={onUpdated}
          onOpen={() => setOpened(opened.includes(r.id) ? opened.filter((x) => x !== r.id) : [...opened, r.id])} />
      ))}
    </section>
  );
};

const AutoStatus = ({ auto, isAdmin }) => (
  <div className="no-print flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 bg-[#0F172A] border border-slate-800 rounded px-3 py-2" data-testid="auto-scan-status">
    <Clock className="h-3.5 w-3.5 text-emerald-400" />
    <span>Auto scan: <span className="text-white">{auto.schedule.scan}</span> (your default rules)</span>
    <span>Last run: <span className="text-white num">{auto.scan ? `${auto.scan.date} • ${auto.scan.rated ?? 0} races scanned & rated` : "not yet"}</span></span>
    {isAdmin && auto.scan?.warning && <span className="text-amber-300" title={auto.scan.warning}>Partial scan: see warning</span>}
  </div>
);

export default function FinalSheets() {
  const isAdmin = useAuth().user?.role === "admin";
  const [races, setRaces] = useState(null);
  const [day, setDay] = useState(auDate(0));
  const [meeting, setMeeting] = useState(null);
  const [scope, setScope] = useState("all");
  const [auto, setAuto] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [features, setFeatures] = useState([]);
  const [feature, setFeature] = useState(null);

  useEffect(() => {
    api.get("/races", { params: RECENT() })
      .then((r) => setRaces(r.data.filter((x) => x.evaluation.ratings?.length)))
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/auto/status").then((r) => setAuto(r.data)).catch(() => {});
    api.get("/featured").then((r) => setFeatures(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!printing) return;
    const t = setTimeout(() => { window.print(); setPrinting(false); }, 300);
    return () => clearTimeout(t);
  }, [printing]);

  const groups = useMemo(() => byMeeting((races || []).filter((r) => r.date === day && (!feature || r.featured === feature.label)
    && (scope === "all" || r.evaluation.preferred))), [races, day, scope, feature]);
  const picked = groups.find((g) => g.key === meeting);
  const shown = picked ? picked.races : groups.flatMap((g) => g.races);
  const pickDay = (d) => { setFeature(null); setDay(d); setMeeting(null); };

  if (!races) return <div className="py-20 grid place-items-center"><div className="loader-bar" /></div>;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up no-print">
        <div>
          <div className="eyebrow mb-2" data-testid="sheets-view-mode">{scope === "all" ? "All races" : "Preferred races"} • {dayLabel(day)}{picked ? ` • ${picked.venue}` : " • meetings"}</div>
          <h1 className="text-4xl sm:text-5xl font-bold" data-testid="sheets-title">Final Sheets</h1>
          <p className="text-slate-400 mt-1 text-sm">Rating and $ price for every runner, top rater = 100.</p>
        </div>
        <Button onClick={() => setPrinting(true)} disabled={!shown.length} data-testid="sheets-print-button"><Printer className="h-4 w-4 mr-2" />{picked ? "Print meeting" : "Print / Save PDF"}</Button>
      </div>
      {auto && <AutoStatus auto={auto} isAdmin={isAdmin} />}
      <div className="flex flex-wrap items-center gap-2 no-print" data-testid="sheets-day-filter">
        <span className="eyebrow mr-2">Race day</span>
        {[0, 1].map((o) => <Chip key={o} on={day === auDate(o) && !feature} onClick={() => pickDay(auDate(o))} testId={`sheets-day-${o}`}>{["Today", "Tomorrow"][o]} • {dayLabel(auDate(o))}</Chip>)}
        {features.map((f, i) => <FeatureButton key={f.label} f={f} on={feature?.label === f.label} testId={`sheets-feature-${i}`}
          onClick={() => { setFeature(f); setDay(f.date); setMeeting(null); setScope("all"); }} />)}
        <span className="eyebrow ml-4 mr-2">Show</span>
        {[["all", "All races"], ["preferred", "Preferred races"]].map(([k, l]) => <Chip key={k} on={scope === k} onClick={() => { setScope(k); setMeeting(null); }} testId={`sheets-scope-${k}`}>{l}</Chip>)}
      </div>
      <div className="space-y-4 no-print">
        <div className="eyebrow" data-testid="sheets-day-summary">{groups.length} meeting{groups.length === 1 ? "" : "s"} • {groups.reduce((n, g) => n + g.races.length, 0)} {scope === "all" ? "rated" : "preferred"} races</div>
        {!groups.length && (
          <div className="panel p-10 text-center text-slate-400" data-testid="sheets-empty">
            No rated races for this day yet. <Link to="/program" className="text-emerald-400">Scan &amp; Rate</Link> to find some.
          </div>
        )}
        {picked
          ? <MeetingSheets key={picked.key} g={picked} onBack={() => setMeeting(null)} onUpdated={(u) => setRaces((rs) => rs.map((x) => (x.id === u.id ? u : x)))} />
          : <MeetingCards groups={groups} onOpen={(k) => { setMeeting(k); window.scrollTo({ top: 0, behavior: "smooth" }); }} prefix="sheets" />}
      </div>
      {printing && (
        <div className="print-block space-y-4" data-testid="sheets-print-area">
          <h1 className="text-2xl font-bold mb-4">Final Sheets — {dayLabel(day)}{picked ? ` • ${picked.venue}` : ""}</h1>
          {shown.map((r, i) => <RaceSheet key={r.id} race={r} i={i} />)}
        </div>
      )}
    </div>
  );
}
